<script lang="ts" setup>
import { addStickyClass } from '~/utils/addStickyClass'
import { Routes } from '~/enums/Routes'
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import NavbarJav from '~/components/PerkuJav/NavbarJav.vue'
import fbIconWhite from '~/assets/icons/fb-white.svg'

import LayoutEnum from '~/enums/LayoutEnum'
import { useStoreConfig } from '~/store/storeConfig'
import { PagesPrefixEnum } from '~/enums/PagesPrefixEnum'

const { type } = defineProps<{ type?: LayoutEnum.Market | LayoutEnum.Quote }>()
const { isTarpininke, isAlt2, isNoriuLive } = useTheme()
const headerRef = ref<HTMLDivElement>()
const placeholderRef = ref<HTMLDivElement>()
const logos = await useAssetManager().getLogos()
const route = useRoute()
const mpLock = useMarketplaceLock()

const scrollHandler = () => addStickyClass(headerRef.value, placeholderRef.value)
const storeConfig = useStoreConfig()
const { getSlug } = useHowTo()

onMounted(() => {
  addStickyClass(headerRef.value)
  window?.addEventListener('scroll', scrollHandler)
})

onBeforeUnmount(() => {
  window?.removeEventListener('scroll', scrollHandler)
})

const isHome = computed(() => route.name === `${PagesPrefixEnum}${Routes.Index}`)
</script>

<template>
  <header
    :key="storeConfig.getIsMobile.valueOf.toString()"
    :class="{ 'desktop-header': !storeConfig.getIsMobile, [type!]: Boolean(type), 'home': isHome }">
    <section ref="headerRef" class="flex justify-center px-2 lg:px-4">
      <div class="flex items-center max-w-xlg w-full lg:px-[15px]">
        <NuxtLink
          :to="getLocalizedRoute(Routes.Index)"
          :class="{
            'lg:my-[18px]': isNoriuLive || isTarpininke,
          }">
          <img
            :src="isNoriuLive && isHome ? logos.logoWhite : logos.logo"
            class="logo"
            :class="{
              'min-w-[219px] max-w-[219px]': !isTarpininke,
              'min-h-[65px] min-w-[90px]': isTarpininke,
            }"
            alt="Logo" />
        </NuxtLink>
        <NavbarJav v-if="!isNoriuLive" :type="type" />

        <div class="profile flex items-center h-full justify-end">
          <template v-if="isAlt2">
            <NuxtLink :href="getFbGroupLink()" target="_blank" class="fb-link">
              <img :src="fbIconWhite" class="w-7 h-7 mr-2" />
            </NuxtLink>
            <hr class="vertical-divider" />
          </template>
          <template v-if="isTarpininke">
            <HeaderHowTo :slug="getSlug()" />
            <hr class="vertical-divider" />
          </template>
          <HeaderTracking />
          <hr class="vertical-divider" />
          <ClientOnly>
            <SearchBar v-if="isTarpininke ? mpLock.isUnlocked.value : !isAlt2" />
          </ClientOnly>
          <HeaderAccount />
          <ClientOnly>
            <HeaderCart v-if="isTarpininke ? mpLock.isUnlocked.value : !isAlt2" />
          </ClientOnly>
        </div>
      </div>
    </section>
    <div ref="placeholderRef" class="header-placeholder" />
  </header>
</template>

<style lang="postcss" scoped>
.alt .desktop-header {
  border-bottom: 1px solid theme('colors.neutral6');
  background-color: theme('colors.brand.primary');
  width: 100%;

  .vertical-divider {
    box-sizing: border-box;
    margin: 0 1rem 0 1rem;
    height: 23px;
    width: 1px;
  }

  .profile {
    gap: 8px;
    margin-left: auto;
    padding-left: 2rem;
    max-width: 750px;
    width: 100%;
  }

  .sticky {
    position: fixed !important;
    z-index: 33;
    background-color: theme('colors.brand.primary');
    width: 100%;
    left: 0;
    top: 0;

    & + .header-placeholder {
      height: 67px;
    }
  }
}

.tp .desktop-header {
  .sticky {
    & + .header-placeholder {
      height: 106px !important;
    }
  }

  .profile {
    max-width: 1000px;
  }
}

.nl .desktop-header {
  border-bottom: 1px solid theme('colors.brown');
  background-color: theme('colors.brand.primaryLight');

  .fb-link {
    @apply invert;
  }

  hr {
    @apply border-black;
  }

  :deep(.header-tracking-component) {
    a {
      @apply text-black font-medium;
    }

    .icon {
      svg {
        path {
          @apply fill-black;
        }
      }
    }
  }

  &.home {
    background-color: theme('colors.brand.primary') !important;

    hr {
      @apply border-white;
    }

    .fb-link {
      @apply invert-0;
    }

    :deep(.account-toggle) {
      .account-icon svg path {
        fill: theme('colors.white') !important;
      }

      .account-badge {
        background-color: theme('colors.white') !important;
      }

      .account-text {
        > span {
          color: theme('colors.white') !important;
        }
      }

      &.active,
      &:hover {
        .account-icon svg path {
          @apply fill-black !important;
        }

        .account-text {
          > span {
            color: theme('colors.black') !important;
          }
        }
      }
    }

    :deep(.header-tracking-component) {
      a {
        @apply text-white;
      }

      .icon {
        svg {
          path {
            @apply fill-white;
          }
        }
      }
    }
  }

  .sticky {
    border-bottom: 1px solid theme('colors.brown');
    background-color: theme('colors.brand.primaryLight');
  }

  &.home {
    .sticky {
      background-color: theme('colors.brand.primary');
    }
  }

  .sticky + .header-placeholder {
    height: 95px !important;
  }
}
</style>
